import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../../../utils/Auth';
import { ColorAndGradientToggles, ColorPickers, Rotation, cornersEyeTypes } from '../helper';
import styles from './CornerEye.module.css';

const getInitialColor = (options, isSecondColor = false) => {
    const colorStops = options?.gradient?.colorStops;
    if (isSecondColor) {
        return colorStops?.[1]?.color || '#000000';
    }
    return options?.color || colorStops?.[0]?.color || '#000000';
};

// Main Component
export default function CornerEye() {
    const { qr_options, optionsDispatch } = useAuth();
    const cornersDotOptions = qr_options?.cornersDotOptions;

    const colorType = useMemo(() => cornersDotOptions?.gradient ? 'gradient' : 'single', [cornersDotOptions?.gradient]);
    const gradientType = useMemo(() => cornersDotOptions?.gradient?.type || 'linear', [cornersDotOptions?.gradient?.type]);
    const firstColor = useMemo(() => getInitialColor(cornersDotOptions), [cornersDotOptions]);
    const secondColor = useMemo(() => getInitialColor(cornersDotOptions, true), [cornersDotOptions]);
    const rotation = useMemo(() => cornersDotOptions?.gradient?.rotation || '0', [cornersDotOptions?.gradient?.rotation]);
    
    const [ displayColorPicker, setDisplayColorPicker ] = useState(null);

    const handleCornersEyeTypeChange = (newType) => {
        optionsDispatch({ type: 'eye', options: { type: newType }});
    };

    const handleColorTypeChange = (e) => {
        const newColorType = e.target.checked ? 'gradient' : 'single';

        if (newColorType === 'single') {
            optionsDispatch({ type: 'eye', options: { color: firstColor, gradient: null }});
        } else {
            optionsDispatch({ type: 'eye', options: { color: null, gradient: { colorStops: [{ offset: 0, color: firstColor }, { offset: 1, color: secondColor }], type: gradientType }}});
        };
    };

    const handleGradientTypeChange = (e) => {
        const newGradientType = e.target.checked ? 'radial' : 'linear';
        optionsDispatch({ type: 'eye', options: { gradient: { ...cornersDotOptions.gradient, type: newGradientType }}});
    };

    const handleColorChange = useCallback((color, name) => {
        if (colorType === 'single') {
            optionsDispatch({ type: 'eye', options: { color: color }});
        } else {
            if (name === 'single-color') {
                optionsDispatch({ type: 'eye', options: { gradient: { ...cornersDotOptions.gradient, colorStops: [{ offset: 0, color: color }, { offset: 1, color: secondColor }]}}});
            } else if (name === 'gradient-color') {
                optionsDispatch({ type: 'eye', options: { gradient: { ...cornersDotOptions.gradient, colorStops: [{ offset: 0, color: firstColor }, { offset: 1, color: color }]}}});
            };
        };
    },[colorType, firstColor, secondColor, cornersDotOptions, optionsDispatch]);

    const handleRotationChange = (e) => {
        const rotation = e.target.value;
        optionsDispatch({ type: 'eye', options: { gradient: { ...cornersDotOptions.gradient, rotation: rotation }}});
    };

    return (
        <div className={styles.container}>
            <div className={styles.inputGroup} style={{marginBottom: '0'}}>
                <div className={styles.label}>Eye Style</div>
                <div className={styles.cornersWrap}>
                    {cornersEyeTypes.map(type => (
                        <div key={type.value} className={cornersDotOptions?.type === type.value ? `${styles.corners} ${styles.selected}` : styles.corners} onClick={() => handleCornersEyeTypeChange(type.value)}>
                            <img src={type.imgSrc} alt='type of corner eye' />
                            <p>{type.name}</p>
                        </div>
                    ))}
                </div>
            </div>
            <div className={styles.inputGroup} style={{flexDirection: 'row', gap: '0 1.25rem'}}>
                <ColorAndGradientToggles
                    colorType={colorType}
                    handleColorTypeChange={handleColorTypeChange}
                    gradientType={gradientType}
                    handleGradientTypeChange={handleGradientTypeChange}
                />
                <Rotation
                    show={colorType === 'gradient' && gradientType === 'linear'}
                    rotation={rotation}
                    handleRotationChange={handleRotationChange}
                />
            </div>
            <div className={styles.inputGroup} style={{marginBottom: '0'}}>
                <div className={styles.label}>Eyes {colorType === 'single' ? 'Color' : 'Gradient'}</div>
                <ColorPickers 
                    colorType={colorType}
                    firstColor={firstColor}
                    secondColor={secondColor}
                    handleColorChange={handleColorChange}
                    displayColorPicker={displayColorPicker}
                    setDisplayColorPicker={setDisplayColorPicker}
                />
            </div>
        </div>
    );
};